import { DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

type Props = {
  /** True when the panel it controls is showing. */
  open: boolean;
  onToggle: () => void;
  /** `id` of the panel this button shows and hides. */
  controls: string;
};

/**
 * Narrow-viewport disclosure control for a panel that is CONTEXT rather than
 * content — the Workflow Details card, the form builder's field palette.
 *
 * The button is `display: none` above 1023px (`.wf-collapse-toggle` in
 * global.css), and the rule that acts on its state — `.wf-collapsible
 * [data-collapsed='true']` — lives in the same band. So a desktop never hides
 * the panel however the flag happens to be sitting, and this control is out of
 * the accessibility tree there rather than being a no-op a screen reader still
 * announces. That is also why there is no width read anywhere near it: a width
 * read is a snapshot taken at render and survives neither rotation, split-view,
 * nor a desktop at 200% zoom.
 */
export function CollapseToggle({ open, onToggle, controls }: Props) {
  const { t } = useTranslation();
  const label = t(open ? 'common.collapse' : 'common.expand');
  return (
    <button
      type="button"
      className="wf-collapse-toggle"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={label}
      title={label}
      onClick={onToggle}
    >
      <DownOutlined
        style={{
          transition: 'transform 140ms ease',
          transform: open ? 'rotate(180deg)' : 'none',
        }}
      />
    </button>
  );
}
